import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ChevronRight,
  ClipboardCheck,
  FileText,
  Paperclip,
} from 'lucide-react';
import {
  STATIC_FIELD_TYPES,
  type RejectEmailRecipient,
  type TaskReviewView,
} from '@hcc/types';
import { Button } from '../../components/ui/Button';
import { Card, CardSection } from '../../components/ui/Card';
import { EmptyState } from '../../components/ui/EmptyState';
import { Skeleton } from '../../components/ui/Skeleton';
import { FieldView } from '../../components/onboarding/formfields/FieldView';
import type { FieldValue } from '../../components/onboarding/formfields/formValues';
import { RejectTaskModal } from '../../components/onboarding/RejectTaskModal';
import {
  useApproveTask,
  useRejectTask,
  useTaskReviewDetail,
} from '../../hooks/useTaskReviews';
import { formatDateTime } from '../../lib/dateFmt';
import { ApiError } from '../../lib/api';

/**
 * Dedicated review screen for one awaiting-review task. HR lands here from the
 * queue to see exactly what an inductee submitted (a filled-in form, a signed
 * document, uploaded files), then approves or requests changes. The reviewable
 * content is resolved server-side and rendered by kind below.
 */
export function TaskReviewDetail() {
  const { personId, taskId } = useParams<{ personId: string; taskId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useTaskReviewDetail(personId, taskId);
  const approve = useApproveTask();
  const reject = useRejectTask();
  const [rejecting, setRejecting] = useState(false);

  async function onApprove() {
    if (!personId || !taskId || !data) return;
    try {
      await approve.mutateAsync({ personId, taskId });
      toast.success(`Approved: ${data.task.title}`);
      navigate('/admin/task-reviews');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'approve failed');
    }
  }

  async function onReject(reason: string, recipient: RejectEmailRecipient) {
    if (!personId || !taskId || !data) return;
    try {
      await reject.mutateAsync({ personId, taskId, reason, recipient });
      const to =
        recipient === 'work'
          ? data.person.email
          : data.person.personal_email ?? data.person.email;
      toast.success(`Sent. The reason will be emailed to ${to}.`);
      setRejecting(false);
      navigate('/admin/task-reviews');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'reject failed');
    }
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <Link
          to="/admin/task-reviews"
          className="inline-flex items-center gap-1 text-sm font-medium text-fg-muted hover:text-fg"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to queue
        </Link>

        {isLoading ? (
          <div className="mt-6 space-y-3">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : error || !data ? (
          <div className="mt-6">
            <EmptyState
              icon={<ClipboardCheck className="h-6 w-6" />}
              title="Nothing to review here"
              description={
                error instanceof ApiError
                  ? error.message
                  : 'This task is no longer awaiting review.'
              }
            />
          </div>
        ) : (
          <>
            <ReviewHeader data={data} />
            <div className="mt-5">
              <ReviewBody data={data} personId={personId!} />
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <Button variant="primary" size="md" onClick={onApprove} loading={approve.isPending}>
                Approve
              </Button>
              <Button
                variant="secondary"
                size="md"
                onClick={() => setRejecting(true)}
                disabled={approve.isPending}
              >
                Request changes
              </Button>
            </div>
          </>
        )}
      </div>

      {data ? (
        <RejectTaskModal
          open={rejecting}
          taskTitle={data.task.title}
          workEmail={data.person.email}
          personalEmail={data.person.personal_email}
          busy={reject.isPending}
          onCancel={() => setRejecting(false)}
          onSubmit={onReject}
        />
      ) : null}
    </main>
  );
}

function ReviewHeader({ data }: { data: TaskReviewView }) {
  const { person, task } = data;
  return (
    <header className="mt-4">
      <Link
        to={`/admin/people/${person.id}`}
        className="inline-flex items-center gap-1 text-xs font-medium text-fg-muted hover:text-fg"
      >
        {person.full_name}
        <ChevronRight className="h-3 w-3" />
        <span>{person.position ?? 'Position unknown'}</span>
      </Link>
      <h1 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
        {task.title}
      </h1>
      {task.description ? (
        <p className="mt-1 whitespace-pre-wrap text-sm text-fg-muted">{task.description}</p>
      ) : null}
      <div className="mt-2 text-[11px] tabular-nums text-fg-muted">
        Submitted {task.submitted_at ? formatDateTime(task.submitted_at) : '-'}
      </div>
      {task.submission_note ? (
        <p className="mt-2 rounded-md bg-surface-2 px-2.5 py-1.5 text-xs text-fg-muted">
          Inductee note: {task.submission_note}
        </p>
      ) : null}
    </header>
  );
}

function ReviewBody({ data, personId }: { data: TaskReviewView; personId: string }) {
  const { detail, task } = data;

  switch (detail.kind) {
    case 'form':
      return <FormReview detail={detail} personId={personId} />;
    case 'document':
      return <DocumentReview detail={detail} personId={personId} />;
    case 'upload':
      return <UploadReview detail={detail} personId={personId} photoKey={task.submission_document_s3_key} />;
    case 'capture':
      return <CaptureReview detail={detail} />;
    case 'simple':
    default:
      return (
        <Card>
          <CardSection>
            <p className="text-sm text-fg-muted">
              The inductee marked this complete. There's no attachment to review:
              approve to clear it, or request changes if something's missing.
            </p>
            {task.submission_document_s3_key ? (
              <PhotoLink personId={personId} photoKey={task.submission_document_s3_key} />
            ) : null}
          </CardSection>
        </Card>
      );
  }
}

function FormReview({
  detail,
  personId,
}: {
  detail: Extract<TaskReviewView['detail'], { kind: 'form' }>;
  personId: string;
}) {
  const fields = detail.schema.pages.flatMap((p) => p.fields);
  const renderable = fields.filter((f) => !STATIC_FIELD_TYPES.includes(f.type));

  return (
    <Card>
      <CardSection>
        <div className="text-xs font-medium uppercase tracking-wider text-fg-dim">
          {detail.title}
        </div>
        {renderable.length === 0 ? (
          <p className="mt-2 text-sm text-fg-muted">This form has no answerable fields.</p>
        ) : (
          <div className="mt-3 space-y-3">
            {renderable.map((f) =>
              f.type === 'file_upload' ? (
                <div key={f.key} className="text-sm">
                  <span className="font-medium text-fg-muted">{f.label}</span>
                  <div className="mt-1 space-y-1">
                    {(detail.files[f.key] ?? []).length === 0 ? (
                      <span className="text-fg-dim">No file uploaded</span>
                    ) : (
                      (detail.files[f.key] ?? []).map((file) => (
                        <FileLink
                          key={file.key}
                          href={`/api/admin/tasks/review/${personId}/files/${file.key}`}
                          name={file.name}
                        />
                      ))
                    )}
                  </div>
                </div>
              ) : (
                <FieldView
                  key={f.key}
                  field={f}
                  value={(detail.values[f.key] as FieldValue) ?? null}
                  onChange={() => {}}
                  disabled
                />
              ),
            )}
            {detail.consent ? (
              <div className="text-xs text-success-soft-fg">
                ✓ Consent given for special-category data
              </div>
            ) : null}
          </div>
        )}
      </CardSection>
    </Card>
  );
}

function DocumentReview({
  detail,
  personId,
}: {
  detail: Extract<TaskReviewView['detail'], { kind: 'document' }>;
  personId: string;
}) {
  const href = detail.signed_pdf_s3_key
    ? `/api/admin/tasks/review/${personId}/signed/${detail.signed_pdf_s3_key}`
    : null;
  return (
    <Card>
      <CardSection>
        <div className="text-xs font-medium uppercase tracking-wider text-fg-dim">
          {detail.title}
        </div>
        {detail.selected_option ? (
          <p className="mt-2 text-sm">
            <span className="text-fg-muted">Selected option:</span>{' '}
            <span className="font-medium">{detail.selected_option}</span>
          </p>
        ) : null}
        {href ? (
          <>
            <object
              data={href}
              type="application/pdf"
              className="mt-3 h-[28rem] w-full rounded-token-sm border border-line/10"
            >
              <p className="p-4 text-sm text-fg-muted">
                Preview unavailable.{' '}
                <a href={href} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                  Open the signed PDF
                </a>
                .
              </p>
            </object>
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
            >
              <FileText className="h-4 w-4" />
              Open the signed PDF in a new tab
            </a>
          </>
        ) : (
          <p className="mt-2 text-sm text-fg-muted">No signed PDF was recorded for this submission.</p>
        )}
      </CardSection>
    </Card>
  );
}

function UploadReview({
  detail,
  personId,
  photoKey,
}: {
  detail: Extract<TaskReviewView['detail'], { kind: 'upload' }>;
  personId: string;
  photoKey: string | null;
}) {
  return (
    <Card>
      <CardSection>
        <div className="text-xs font-medium uppercase tracking-wider text-fg-dim">
          Uploaded files
        </div>
        {detail.files.length === 0 ? (
          <p className="mt-2 text-sm text-fg-muted">No files were attached.</p>
        ) : (
          <div className="mt-2 space-y-1">
            {detail.files.map((file) => (
              <FileLink
                key={file.key}
                href={`/api/admin/tasks/review/${personId}/files/${file.key}`}
                name={file.name}
              />
            ))}
          </div>
        )}
        {photoKey ? <PhotoLink personId={personId} photoKey={photoKey} /> : null}
      </CardSection>
    </Card>
  );
}

function CaptureReview({
  detail,
}: {
  detail: Extract<TaskReviewView['detail'], { kind: 'capture' }>;
}) {
  const entries = Object.entries(detail.values);
  return (
    <Card>
      <CardSection>
        <div className="text-xs font-medium uppercase tracking-wider text-fg-dim">
          Captured details
        </div>
        {entries.length === 0 ? (
          <p className="mt-2 text-sm text-fg-muted">No captured values to show.</p>
        ) : (
          <dl className="mt-2 space-y-1 text-sm">
            {entries.map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <dt className="text-fg-muted">{k.replace(/_/g, ' ')}:</dt>
                <dd className="font-medium">{String(v)}</dd>
              </div>
            ))}
          </dl>
        )}
      </CardSection>
    </Card>
  );
}

function FileLink({ href, name }: { href: string; name: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-1.5 text-sm text-accent hover:underline"
    >
      <Paperclip className="h-3.5 w-3.5" />
      {name}
    </a>
  );
}

function PhotoLink({ personId, photoKey }: { personId: string; photoKey: string }) {
  void personId;
  return (
    <a
      href={`/api/documents/${photoKey}`}
      target="_blank"
      rel="noreferrer"
      className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
    >
      <Paperclip className="h-3.5 w-3.5" />
      View submitted photo
    </a>
  );
}
